import { useState } from 'react';
import { Recipe } from '@core/shopping';
import { EXAMPLE_RECIPES, ExampleRecipe } from '@data/exampleRecipes';

interface RecipeFormProps {
  onAddRecipe: (recipe: Recipe) => void;
}

export function RecipeForm({ onAddRecipe }: RecipeFormProps) {
  const [name, setName] = useState('');
  const [servings, setServings] = useState('4');
  const [ingredients, setIngredients] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'dinner' | 'breakfast' | 'dessert' | 'sides'>('all');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !ingredients.trim()) {
      return;
    }

    const recipe: Recipe = {
      id: `recipe-${Date.now()}`,
      name: name.trim(),
      servings: parseInt(servings) || 4,
      ingredientLines: ingredients
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0),
    };

    onAddRecipe(recipe);

    // Reset form
    setName('');
    setServings('4');
    setIngredients('');
  };

  const handleAddExampleRecipe = (example: ExampleRecipe) => {
    const recipe: Recipe = {
      id: `recipe-${Date.now()}`,
      name: example.name,
      servings: example.servings,
      ingredientLines: example.ingredients,
    };
    onAddRecipe(recipe);
  };

  const filteredRecipes = selectedCategory === 'all'
    ? EXAMPLE_RECIPES
    : EXAMPLE_RECIPES.filter(r => r.category === selectedCategory);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Manual Entry Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Add Custom Recipe</h2>
        </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipe Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Chocolate Chip Cookies"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Servings
            </label>
            <input
              type="number"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min="1"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ingredients (one per line)
          </label>
          <textarea
            value={ingredients}
            onChange={(e) => setIngredients(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder="2 cups flour&#10;1 cup sugar&#10;2 eggs"
            rows={8}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Each line will be parsed automatically. Supports fractions (½, 1/2), ranges (2-3), and units (cup, lb, oz).
          </p>
        </div>

        <button
          type="submit"
          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Add Recipe
        </button>
      </form>
      </div>

      {/* Example Recipe Browser */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Example Recipes</h2>
        <p className="text-sm text-gray-600 mb-6">
          Quick-add curated recipes to test the parser
        </p>

        {/* Category Filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {(['all', 'dinner', 'breakfast', 'dessert', 'sides'] as const).map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Recipe Cards */}
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {filteredRecipes.map((recipe) => (
            <div
              key={recipe.id}
              className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm">{recipe.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{recipe.description}</p>
                </div>
              </div>

              <div className="text-xs text-gray-600 mb-2">
                <span className="font-medium">{recipe.servings}</span> servings •
                <span className="ml-1">{recipe.ingredients.length} ingredients</span>
              </div>

              <button
                onClick={() => handleAddExampleRecipe(recipe)}
                className="w-full px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                Add to Recipes
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
